import { BookOpenText, FileText, Layers3, RotateCcw, X } from "lucide-react";
import type { HubCatalog, SearchResultType } from "../lib/types";

type KnowledgeSidebarProps = {
  catalog: HubCatalog;
  activeSectionId: string;
  activeType: "all" | SearchResultType;
  mobileOpen: boolean;
  onSectionChange: (sectionId: string) => void;
  onTypeChange: (type: "all" | SearchResultType) => void;
  onReset: () => void;
  onClose: () => void;
};

const contentTypes: Array<{
  value: "all" | SearchResultType;
  label: string;
  icon: typeof Layers3;
}> = [
  { value: "all", label: "All content", icon: Layers3 },
  { value: "module", label: "Topics", icon: BookOpenText },
  { value: "chapter", label: "Guidance chapters", icon: FileText },
  { value: "resource", label: "Downloads", icon: FileText },
];

export function KnowledgeSidebar({
  catalog,
  activeSectionId,
  activeType,
  mobileOpen,
  onSectionChange,
  onTypeChange,
  onReset,
  onClose,
}: KnowledgeSidebarProps) {
  return (
    <>
      <aside className={["knowledge-sidebar", mobileOpen ? "knowledge-sidebar--open" : ""].filter(Boolean).join(" ")}>
        <div className="knowledge-sidebar__mobile-heading">
          <strong>Browse MCS with Lwazi</strong>
          <button type="button" onClick={onClose} aria-label="Close knowledge areas"><X size={19} /></button>
        </div>
        <div className="knowledge-sidebar__intro">
          <span className="knowledge-sidebar__icon"><img src="/lwazi/reading.png" alt="" aria-hidden="true" /></span>
          <div>
            <p>Lwazi's library</p>
            <h2>Knowledge areas</h2>
          </div>
        </div>
        <div className="knowledge-sidebar__list">
          <button
            className={!activeSectionId ? "knowledge-area is-selected" : "knowledge-area"}
            type="button"
            onClick={() => onSectionChange("")}
          >
            <span><strong>All MCS guidance</strong><small>Search the complete public corpus</small></span>
            <b>{catalog.course.moduleCount}</b>
          </button>
          {catalog.sections.map((section, index) => (
            <button
              className={[
                "knowledge-area",
                `knowledge-area--accent-${(index % 4) + 1}`,
                activeSectionId === section.id ? "is-selected" : "",
              ].filter(Boolean).join(" ")}
              key={section.id}
              type="button"
              onClick={() => onSectionChange(section.id)}
            >
              <span><strong>{section.title}</strong><small>{section.moduleCount} topic{section.moduleCount === 1 ? "" : "s"}</small></span>
              <b>{section.moduleCount}</b>
            </button>
          ))}
        </div>

        <div className="knowledge-sidebar__divider" />

        <fieldset className="content-type-filter">
          <legend>Content type</legend>
          {contentTypes.map(({ value, label, icon: Icon }) => (
            <label className={activeType === value ? "is-selected" : ""} key={value}>
              <input
                type="radio"
                name="content-type"
                checked={activeType === value}
                onChange={() => onTypeChange(value)}
              />
              <Icon size={16} aria-hidden="true" />
              <span>{label}</span>
            </label>
          ))}
        </fieldset>

        {(activeSectionId || activeType !== "all") ? (
          <button className="sidebar-reset" type="button" onClick={onReset}>
            <RotateCcw size={15} aria-hidden="true" />
            Reset filters
          </button>
        ) : null}

        <div className="knowledge-sidebar__stats">
          <span><strong>{catalog.course.chapterCount}</strong> chapters</span>
          <span><strong>{catalog.course.resourceCount}</strong> resources</span>
        </div>
      </aside>
      {mobileOpen ? <button className="sidebar-scrim" type="button" onClick={onClose} aria-label="Close knowledge areas" /> : null}
    </>
  );
}
